import { getSiteUrl } from "@clockwise/client-commons/src/config/api";
import { Close } from "@clockwise/design-system/icons";
import classNames from "classnames";
import React from "react";
import PrismCube from "../../assets/banners/PrismCube.gif";
import RescheduleBanner from "../../assets/banners/RescheduleBanner.svg";
import { appPaths, webappDefaultWindowName } from "../../constants/route.constants";
import { TrackingEvents, track } from "../../util/analytics.util";

export enum SidebarAIBannerVariant {
  Variation_B = "Variation_B",
  Prism = "Prism",
}

const BACKGROUND_COLOR: { [v in SidebarAIBannerVariant]: string } = {
  Variation_B: "cw-bg-accent-emphasis cw-border-accent-emphasis cw-pb-4 cw-border cw-border-solid",
  Prism: "cw-bg-accent cw-border-accent",
};

const HEADER_COLOR: { [v in SidebarAIBannerVariant]: string } = {
  Variation_B: "cw-text-onEmphasis",
  Prism: "cw-text-onEmphasis cw-text-13",
};

const BUTTON_COLORS: { [v in SidebarAIBannerVariant]: string } = {
  Variation_B: "cw-text-brand-pressed cw-bg-default hover:cw-bg-default-hover cw-px-2 cw-py-3",
  Prism: "cw-text-brand-pressed cw-bg-accent hover:cw-bg-[#A3E573] cw-px-2 cw-py-1.5",
};

const COPY: { [v in SidebarAIBannerVariant]: string } = {
  Variation_B: "Offload scheduling for good",
  Prism: "Meet your intelligent calendar",
};

export const ChatBannerExperiment = ({
  onDismiss,
  variant,
}: {
  onDismiss: () => void;
  variant: SidebarAIBannerVariant;
}) => {
  const handleDismiss = () => {
    track(TrackingEvents.CHAT.TRY_AI_BANNER.DISMISSED);
    onDismiss();
  };

  const handleClick = () => {
    track(TrackingEvents.CHAT.TRY_AI_BANNER.CLICKED);
  };

  const isPrismBanner = variant === SidebarAIBannerVariant.Prism;

  const backgroundClasses = BACKGROUND_COLOR[variant];
  const buttonClasses = BUTTON_COLORS[variant];
  const headerClasses = HEADER_COLOR[variant];
  const textForVariant = COPY[variant];
  const imgSrc = isPrismBanner ? PrismCube : RescheduleBanner;

  return (
    <div className="cw-relative cw-group">
      <div
        className={classNames(
          `cw-absolute cw-top-0 cw-right-0
          cw-invisible group-hover:cw-visible cw-cursor-pointer`,
          {
            "cw-text-default-hover group-hover:cw-opacity-50 cw-pt-0.5 cw-pr-3": !isPrismBanner,
            "cw-text-onEmphasis-hover group-hover:cw-opacity-100 cw-pt-2 cw-pr-2": isPrismBanner,
          },
        )}
        onClick={handleDismiss}
      >
        <Close fontSize="small" />
      </div>
      <a
        className={classNames(
          "cw-flex cw-rounded-lg cw-no-underline cw-overflow-hidden",
          "cw-heading-base cw-text-14 cw-leading-tight",
          backgroundClasses,
        )}
        href={`${getSiteUrl()}${appPaths.ai}`}
        target={webappDefaultWindowName}
        onClick={handleClick}
      >
        <div className="cw-flex-col">
          <div className="cw-flex cw-justify-center">
            <img
              src={imgSrc}
              className={classNames("cw-w-[255px] cw-mx-2 -cw-mt-1", {
                "cw-w-full cw-rounded-lg": isPrismBanner,
              })}
            />
          </div>
          <div
            className={classNames(`cw-flex cw-items-center`, {
              "cw-absolute cw-justify-between cw-w-full cw-bottom-0 cw-px-2 cw-pb-2": isPrismBanner,
              "cw-mx-4 cw-mt-4": !isPrismBanner,
            })}
          >
            <div className={classNames(headerClasses, "cw-pr-1 cw-font-semibold")}>
              {textForVariant}
            </div>
            <button
              className={classNames(
                "cw-rounded-lg cw-text-center cw-text-11 cw-flex cw-justify-center cw-border-none",
                "cw-leading-none cw-font-body cw-whitespace-nowrap",
                "cw-flex cw-items-center cw-cursor-pointer cw-font-semibold",
                buttonClasses,
              )}
            >
              {isPrismBanner ? "Try Prism" : "Try AI Scheduler"}
            </button>
          </div>
        </div>
      </a>
    </div>
  );
};
